/**
 * License gate for the web-side AI video maker.
 *
 * The same key that unlocks the Chrome extension unlocks this page. It is
 * pasted once and cached in localStorage so it doesn't have to be re-entered.
 *
 * Two rules the backend imposes, both of which this file exists to respect:
 *
 * 1. Never claim a device seat from the browser. `/api/license/validate` with
 *    `activate: true` burns one of only three seats per browser profile, and
 *    the user would get `device_limit` on their real devices. So the balance
 *    check below sends NO `deviceId`, which hits the route's seat-free branch.
 * 2. `/api/agent/token` is the real gate: it spends a credit atomically and
 *    mints a single-use ephemeral token. The Gemini key never leaves the server.
 */

const STORAGE_KEY = "screenlet:license";

export function readStoredLicense(): string {
	if (typeof window === "undefined") return "";
	try {
		return window.localStorage.getItem(STORAGE_KEY) ?? "";
	} catch {
		return "";
	}
}

export function storeLicense({ license }: { license: string }): void {
	try {
		window.localStorage.setItem(STORAGE_KEY, license.trim());
	} catch {
		/* private mode / storage disabled — the key just won't persist */
	}
}

export function clearStoredLicense(): void {
	try {
		window.localStorage.removeItem(STORAGE_KEY);
	} catch {
		/* ignore */
	}
}

export interface LicenseStatus {
	valid: boolean;
	isPaid: boolean;
	aiVideos: number;
}

/**
 * Read-only balance check. Claims no seat and spends no credit — safe to call
 * whenever the key changes.
 */
export async function checkLicense({
	license,
}: {
	license: string;
}): Promise<LicenseStatus> {
	const response = await fetch("/api/license/validate", {
		method: "POST",
		headers: { "content-type": "application/json" },
		// Deliberately no deviceId — see the seat rule above.
		body: JSON.stringify({ license: license.trim() }),
	});

	if (!response.ok) {
		if (response.status === 429) {
			throw new Error("Too many attempts. Wait a minute and try again.");
		}
		throw new Error("Could not check that license key.");
	}

	const data = (await response.json()) as Partial<LicenseStatus>;
	return {
		valid: data.valid === true,
		isPaid: data.isPaid === true,
		aiVideos: typeof data.aiVideos === "number" ? data.aiVideos : 0,
	};
}

export interface AgentToken {
	token: string;
	model: string;
	expiresAt: string;
	aiVideos: number;
}

const TOKEN_ERRORS: Record<string, string> = {
	invalid_license: "That license key wasn't recognised.",
	no_credits:
		"You're out of AI video credits. Grab a top-up pack from the pricing section.",
	rate_limited: "Too many requests. Wait a minute and try again.",
	server_misconfigured: "The AI service isn't configured. Try again later.",
	mint_failed: "Couldn't reach the AI service. Your credit wasn't used.",
	no_license: "Enter your license key first.",
};

/**
 * Spends one AI video credit and returns a single-use Live token. Must be
 * followed by a connection within a minute — the token's session window is
 * short by design.
 */
export async function mintAgentToken({
	license,
}: {
	license: string;
}): Promise<AgentToken> {
	const response = await fetch("/api/agent/token", {
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify({ license: license.trim() }),
	});

	const data = (await response.json().catch(() => null)) as {
		success?: boolean;
		token?: string;
		model?: string;
		expiresAt?: string;
		aiVideos?: number;
		error?: string;
	} | null;

	if (!response.ok || !data?.success || !data.token) {
		const error = data?.error ?? "";
		throw new Error(TOKEN_ERRORS[error] ?? "Couldn't start the AI session.");
	}

	return {
		token: data.token,
		model: data.model ?? "",
		expiresAt: data.expiresAt ?? "",
		aiVideos: typeof data.aiVideos === "number" ? data.aiVideos : 0,
	};
}
